import { useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useOkapiKy } from '@folio/stripes/core';
import { useQuery } from 'react-query';
import { fetchVocabularies } from '../api/vocabularyService';
var PAGE_SIZE = 50;
/**
 * Hook for vocabulary list page with type filter, text search, and offset pagination.
 * Hits /czna-authorities/vocabularies, which restricts results to VOCABULARY_* types
 * on the server side (including when no `type` filter is provided).
 */
var useVocabularyList = function () {
    var ky = useOkapiKy();
    var location = useLocation();
    var initialParams = useMemo(function () {
        var params = new URLSearchParams(location.search);
        return {
            type: params.get('type') || '',
            query: params.get('query') || '',
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    var _a = useState(initialParams.query), query = _a[0], setQueryState = _a[1];
    var _b = useState(initialParams.type), typeFilter = _b[0], setTypeFilter = _b[1];
    var _c = useState(0), offset = _c[0], setOffset = _c[1];
    var _d = useState('headingValue'), sortBy = _d[0], setSortBy = _d[1];
    var _f = useState('asc'), sortOrder = _f[0], setSortOrder = _f[1];
    var _g = useState(true), filterPaneIsVisible = _g[0], setFilterPaneIsVisible = _g[1];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var handleSort = useCallback(function (_e, _a) {
        var name = _a.name;
        var col = String(name);
        if (col === sortBy) {
            setSortOrder(function (prev) { return (prev === 'asc' ? 'desc' : 'asc'); });
        }
        else {
            setSortBy(col);
            setSortOrder('asc');
        }
        setOffset(0);
    }, [sortBy]);
    var _h = useQuery(['czna-vocabularies', typeFilter, query, sortBy, sortOrder, offset], function () { return fetchVocabularies(ky, {
        type: typeFilter || undefined,
        query: query || undefined,
        sortBy: sortBy,
        sortOrder: sortOrder,
        limit: PAGE_SIZE,
        offset: offset,
    }); }, {
        keepPreviousData: true,
    }), data = _h.data, isLoading = _h.isLoading, isFetching = _h.isFetching, isError = _h.isError, refetch = _h.refetch;
    // CZNA-1670 — a different search text is a different result set: start on its first page,
    // like a change of vocabulary or sort does.
    var setQuery = useCallback(function (value) {
        setQueryState(value);
        setOffset(0);
    }, []);
    var handleClearSearch = useCallback(function () {
        setQueryState('');
        setOffset(0);
    }, []);
    var handleTypeFilterChange = useCallback(function (value) {
        setTypeFilter(value);
        setOffset(0);
    }, []);
    var toggleFilterPane = useCallback(function () {
        setFilterPaneIsVisible(function (prev) { return !prev; });
    }, []);
    var items = useMemo(function () { return (data === null || data === void 0 ? void 0 : data.items) || []; }, [data]);
    return {
        query: query,
        setQuery: setQuery,
        typeFilter: typeFilter,
        handleTypeFilterChange: handleTypeFilterChange,
        offset: offset,
        setOffset: setOffset,
        pageSize: PAGE_SIZE,
        filterPaneIsVisible: filterPaneIsVisible,
        toggleFilterPane: toggleFilterPane,
        handleClearSearch: handleClearSearch,
        items: items,
        totalRecords: (data === null || data === void 0 ? void 0 : data.totalRecords) || 0,
        sortBy: sortBy,
        sortOrder: sortOrder,
        handleSort: handleSort,
        isLoading: isLoading,
        isFetching: isFetching,
        isError: isError,
        refetch: refetch,
    };
};
export default useVocabularyList;
